import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Eye, Copy, Trash } from '@phosphor-icons/react';

const HerokuManageUrls = () => {
  const [deployments, setDeployments] = useState([]);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`/api/heroku/deployments`, { withCredentials: true });
      setDeployments(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to completely delete this app? This will instantly destroy the app from Heroku.")) return;
    try {
      await axios.delete(`/api/heroku/deployments/${id}`, { withCredentials: true });
      toast.success("App deleted successfully");
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete app");
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div>
        <h1 className="text-4xl font-black mb-2 text-[#8b60ed]">MANAGE URLs</h1>
        <p className="text-slate-400 font-mono">View, manage, and delete your deployed Heroku apps.</p>
      </div>
      
      <div className="border border-white/10 bg-card overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-white/10 bg-black/20">
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Date</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">App Name</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Template</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Status</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">URL</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {deployments.map(dep => (
              <tr key={dep.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="p-4 text-sm text-slate-400 font-mono">{format(new Date(dep.created_at), 'yyyy-MM-dd HH:mm')}</td>
                <td className="p-4 font-mono text-sm font-bold text-white">{dep.app_name || '-'}</td>
                <td className="p-4 text-sm">{dep.template_name}</td>
                <td className="p-4 text-sm">
                  <span className={`px-2 py-1 text-xs font-bold tracking-wider ${
                    dep.status === 'live' ? 'bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/20' : 
                    dep.status === 'failed' ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 
                    'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20'
                  }`}>
                    {dep.status}
                  </span>
                </td>
                <td className="p-4 text-sm">
                  {dep.public_url ? (
                    <div className="flex items-center gap-2">
                      <div className="bg-[#0A0B0E] border border-white/10 px-2 py-1 text-xs text-slate-400 font-mono truncate w-48 select-all" title={dep.public_url}>
                        {dep.public_url}
                      </div>
                      <button 
                        onClick={() => { navigator.clipboard.writeText(dep.public_url); toast.success("Copied!"); }}
                        className="text-slate-400 hover:text-white transition-colors p-1"
                      >
                        <Copy size={16} />
                      </button>
                      <a href={dep.public_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#8b60ed] transition-colors p-1">
                        <Eye size={16} />
                      </a>
                    </div>
                  ) : '-'}
                </td>
                <td className="p-4 flex items-center justify-end gap-2">
                  <button 
                    onClick={() => handleDelete(dep.id)} 
                    className="bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500 hover:text-white transition-colors px-3 py-1 font-bold text-xs flex items-center gap-1 uppercase tracking-wider"
                    title="Delete App"
                  >
                    <Trash size={14} /> Delete
                  </button>
                </td>
              </tr>
            ))}
            {deployments.length === 0 && (
              <tr>
                <td colSpan="6" className="p-8 text-center text-slate-500">No deployments found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HerokuManageUrls;